import { useRef, useCallback, useEffect, useMemo } from 'react';
import { Canvas, FabricImage, FabricObject } from 'fabric';
import { ChromaKeyParams } from '../constants/chroma-key-shader';
import {
  createChromaKeyInstance,
  destroyChromaKeyInstance,
  drawChromaKey,
  type ChromaKeyInstance
} from '../utils/chroma-key-instance';
import { useMedia } from '../../media';
import { onModelRendered } from '../../canvas/canvas-events';

type ChromaKeyManager = {
  activate: (videoId: string, videoElement: HTMLVideoElement) => void;
  deactivate: (videoId: string) => void;
  updateParams: (videoId: string, params: Partial<ChromaKeyParams>) => void;
  getChromaCanvas: (videoId: string) => HTMLCanvasElement | null;
  isActive: (videoId: string) => boolean;
};

type ActiveInstance = { instance: ChromaKeyInstance; frameId: number };

function useChromaKeyManager(canvas: Canvas | null): ChromaKeyManager {
  const instancesRef = useRef<Map<string, ActiveInstance>>(new Map());
  // Kept fresh so the render loop can request a Fabric repaint without re-creating instances.
  const fabricCanvasRef = useRef(canvas);
  fabricCanvasRef.current = canvas;

  const stop = useCallback((active: ActiveInstance) => {
    cancelAnimationFrame(active.frameId);
    destroyChromaKeyInstance(active.instance);
  }, []);

  const activate = useCallback((videoId: string, videoElement: HTMLVideoElement) => {
    if (instancesRef.current.has(videoId)) return;

    const instance = createChromaKeyInstance(videoElement);
    if (!instance) return;

    const active: ActiveInstance = { instance, frameId: 0 };

    // Paused videos have no repaint driver, so nudge Fabric after drawing one.
    const loop = () => {
      if (drawChromaKey(instance) && videoElement.paused) fabricCanvasRef.current?.requestRenderAll();
      active.frameId = requestAnimationFrame(loop);
    };

    active.frameId = requestAnimationFrame(loop);
    instancesRef.current.set(videoId, active);
  }, []);

  const deactivate = useCallback(
    (videoId: string) => {
      const active = instancesRef.current.get(videoId);
      if (!active) return;
      stop(active);
      instancesRef.current.delete(videoId);
    },
    [stop]
  );

  const updateParams = useCallback((videoId: string, newParams: Partial<ChromaKeyParams>) => {
    const active = instancesRef.current.get(videoId);
    if (!active) return;
    active.instance.params = { ...active.instance.params, ...newParams };
  }, []);

  const getChromaCanvas = useCallback(
    (videoId: string): HTMLCanvasElement | null => instancesRef.current.get(videoId)?.instance.canvas ?? null,
    []
  );

  const isActive = useCallback((videoId: string): boolean => instancesRef.current.has(videoId), []);

  useEffect(() => {
    const instances = instancesRef.current;
    return () => instances.forEach(stop);
  }, [stop]);

  // Stable identity so consumer effects don't re-fire on every render.
  return useMemo(
    () => ({ activate, deactivate, updateParams, getChromaCanvas, isActive }),
    [activate, deactivate, updateParams, getChromaCanvas, isActive]
  );
}

/**
 * Chroma key renderer — same shape as `use-alpha-falloff`.
 *
 * Source of truth: the model (`VideoContent.chromaKey`). `applyEffects` projects it onto the
 * Fabric object as `chromaKeyEnabled` / `chromaKeyParams` (which also serialise into the
 * canvas JSON for export). `reconcile` reads that projection and converges the GPU instances —
 * derived state that can't live in the model — after the canvas repaints.
 *
 * Public API: `setVideoChroma(videoObj, enabled, params)` — a live preview; the caller commits.
 */

type WithVideoId = FabricObject & { id?: string };

const getVideoId = (obj: FabricObject): string | undefined => (obj as WithVideoId).id;

type UseChromaKeySyncReturn = {
  setVideoChroma: (videoObj: FabricObject, enabled: boolean, params: ChromaKeyParams) => void;
};

export function useChromaKeySync(canvas: Canvas | null): UseChromaKeySyncReturn {
  const manager = useChromaKeyManager(canvas);
  const { getVideo } = useMedia();

  const reconcile = useCallback(() => {
    if (!canvas) return;

    for (const obj of canvas.getObjects()) {
      const id = getVideoId(obj);
      if (!id) continue;
      const videoEl = getVideo(id);
      if (!videoEl) continue; // not a video registered in this session

      const enabled = obj.chromaKeyEnabled === true;
      const params = obj.chromaKeyParams;
      const active = manager.isActive(id);

      if (enabled && !active) {
        manager.activate(id, videoEl);
        const chromaCanvas = manager.getChromaCanvas(id);
        if (chromaCanvas) {
          (obj as FabricImage).setElement(chromaCanvas);
        }
      } else if (!enabled && active) {
        (obj as FabricImage).setElement(videoEl);
        manager.deactivate(id);
      }

      if (enabled && params) {
        manager.updateParams(id, params);
      }
    }

    canvas.requestRenderAll();
  }, [canvas, getVideo, manager]);

  /// Clean up the video's effect instance when the video is removed from the canvas.
  const handleRemoved = useCallback(
    (e: { target?: FabricObject }) => {
      const id = e.target && getVideoId(e.target);
      if (id && manager.isActive(id)) manager.deactivate(id);
    },
    [manager]
  );

  // `object:added` covers initial loads and resolution swaps; `modelRendered` covers undo/redo.
  useEffect(() => {
    if (!canvas) return;
    canvas.on('object:added', reconcile);
    canvas.on('object:removed', handleRemoved);
    const offModelRendered = onModelRendered(canvas, reconcile);
    reconcile();
    return () => {
      canvas.off('object:added', reconcile);
      canvas.off('object:removed', handleRemoved);
      offModelRendered();
    };
  }, [canvas, reconcile, handleRemoved]);

  const setVideoChroma = useCallback<UseChromaKeySyncReturn['setVideoChroma']>(
    (videoObj, enabled, params) => {
      // Live preview: flags on the object + converge the GL. The owner commits to the model.
      videoObj.chromaKeyEnabled = enabled;
      videoObj.chromaKeyParams = params;
      reconcile();
    },
    [reconcile]
  );

  return { setVideoChroma };
}
